import { Grid, Typography } from '@material-ui/core';
import { Cancel } from '@material-ui/icons';
import { Option, tipoInteroperabilidadeOptions } from 'common/genericOptions';
import { useGenericStyles } from 'common/genericStyles';
import {
  AddInteroperabilidadeRequest,
  FuncaoParticipante,
  InstrumentoFinanceiroApiResponse,
  RegistroStatus,
  TipoInteroperabilidade,
} from 'common/genericTypes';
import FinishedSign from 'components/finishedSign';
import { Button, Dropdown, LgSpace, NumberField, Section, SmSpace } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { addCpfCnpjMask } from 'utils/mask';
import { formatNumber } from 'utils/number';
import { isNil } from 'utils/validation';
import InstrumentoAutocomplete from 'components/operacoes/instrumentoAutocomplete';
import { getQuantityType } from 'utils/instrumentoFinanceiroUtils';

const CadastrarOperacaoTransferencia: React.FC = () => {
  const dispatch = useDispatch();

  const getInstrumentosResumeApi = useSelector((state: RootState) => state.instrumentoApi.getInstrumentosResume);
  const getInstrumentoDetalhes = useSelector((state: RootState) => state.instrumentoApi.getInstrumentoDetalhes);
  const getInvestidoresApi = useSelector((state: RootState) => state.investidorApi.getInvestidores);
  const getPosicoes = useSelector((state: RootState) => state.investidorApi.getPosicoes);
  const getParticipantes = useSelector((state: RootState) => state.participanteApi.getParticipantesResume);
  const postOperacoesInteroperabilidadeEntrada = useSelector(
    (state: RootState) => state.operacaoApi.postOperacoesInteroperabilidadeEntrada
  );
  const postOperacoesInteroperabilidadeSaida = useSelector(
    (state: RootState) => state.operacaoApi.postOperacoesInteroperabilidadeSaida
  );

  const classes = useGenericStyles();
  const [instrumentos, setInstrumentos] = useState<Array<InstrumentoFinanceiroApiResponse>>([]);
  const [tipo, setTipo] = useState<string>('');
  const [investidorOptions, setInvestidorOptions] = useState<Array<Option>>();
  const [custodianteOptions, setCustodianteOptions] = useState<Array<Option>>();

  const [instrumento, setInstrumento] = useState<InstrumentoFinanceiroApiResponse | null>(null);
  const [instrumentoInadimplido, setInstrumentoInadimplido] = useState<boolean>(false);
  const [investidorId, setInvestidorId] = useState<string>('');
  const [custodianteId, setCustodianteId] = useState<string>('');
  const [quantidade, setQuantidade] = useState<number>();
  const [quantidadeMax, setQuantidadeMax] = useState<number>();
  const [precoUnitario, setPrecoUnitario] = useState<number>();
  const [finished, setFinished] = useState(false);

  const getInstrumentos = useCallback(async () => {
    try {
      const result = await getInstrumentosResumeApi(dispatch, { registroStatus: [RegistroStatus.Aprovado] }, true);
      setInstrumentos(result);
    } catch { }
  }, [dispatch, getInstrumentosResumeApi]);

  const getCustodiantes = useCallback(() => {
    getParticipantes(dispatch, { funcao: FuncaoParticipante.Custodiante, suspenso: false }, true)
      .then((result) => {
        const options = result?.map((custodiante) => ({
          value: custodiante.id!,
          description: custodiante.razaoSocial,
        }));
        setCustodianteOptions(options);
      })
      .catch();
  }, [dispatch, getParticipantes]);

  const getInvestidores = useCallback(
    async (custodianteId?: string) => {
      try {
        const result = await getInvestidoresApi(dispatch, { participanteId: custodianteId }, true);
        const options = result?.map((posicao) => ({
          value: posicao.id!,
          description: addCpfCnpjMask(posicao.cpfCnpj!),
        }));
        setInvestidorOptions(options);
      } catch { }
    },
    [dispatch, getInvestidoresApi]
  );

  const getPosicao = useCallback(
    async (investidorId: string, instrumentoFinanceiroId: string) => {
      try {
        const posicoes = await getPosicoes(dispatch, { instrumentoFinanceiroId, investidorId });
        if (posicoes.length === 0) {
          setQuantidadeMax(0);
        } else if (posicoes.length === 1) {
          const posicao = posicoes[0];
          setQuantidadeMax(posicao.saldoDisponivel);
        }
      } catch { }
    },
    [dispatch, getPosicoes]
  );

  useEffect(() => {
    getInstrumentos();
    getCustodiantes();
  }, [getInstrumentos, getCustodiantes]);

  useEffect(() => {
    if (instrumento) {
      getInstrumentoDetalhes(dispatch, instrumento.instrumentoFinanceiroGuid)
        .then((result) => setInstrumentoInadimplido(result?.hasPagamentosInadimplentes === true))
        .catch(() => { });
    } else {
      setInstrumentoInadimplido(false);
    }
  }, [instrumento, dispatch, getInstrumentoDetalhes]);

  const handleSubmit = async () => {
    try {
      if (instrumento && investidorId && custodianteId && quantidade && precoUnitario && tipo) {
        const addTransferenciaRequest: AddInteroperabilidadeRequest = {
          tipo: tipo as TipoInteroperabilidade,
          instrumentoFinanceiroId: instrumento.instrumentoFinanceiroGuid,
          investidorId,
          quantidade,
          precoUnitario,
          custodianteId,
        };
        if (tipo === TipoInteroperabilidade.Saida)
          await postOperacoesInteroperabilidadeSaida(dispatch, addTransferenciaRequest, true);
        else if (tipo === TipoInteroperabilidade.Entrada)
          await postOperacoesInteroperabilidadeEntrada(dispatch, addTransferenciaRequest, true);
        setFinished(true);
      }
    } catch (error) { }
  };

  useEffect(() => {
    getInvestidores(custodianteId);
  }, [getInvestidores, custodianteId]);

  useEffect(() => {
    if (tipo === TipoInteroperabilidade.Saida && instrumento) getPosicao(investidorId, instrumento.instrumentoFinanceiroGuid);
  }, [investidorId, instrumento, tipo, setQuantidadeMax, getPosicao]);

  const formFulfilled = !!instrumento && investidorId && custodianteId && quantidade && precoUnitario;

  return (
    <div style={{ marginRight: 40 }}>
      <Typography variant="h1">{'Transferência Interoperabilidade'}</Typography>
      <LgSpace />
      {finished ? (
        <FinishedSign description={'Transferência concluída com sucesso'} redirectRoute={'/operacoes/gerenciar'} />
      ) : (
        <>
          {instrumentoInadimplido && (
            <>
              <SmSpace />
              <div className={classes.errorToast}>
                <Cancel style={{ marginRight: 10 }} />
                <div style={{ display: 'flex', flex: 1 }}>
                  <Typography variant="body2">
                    {
                      'Este Instrumento está Inadimplido, antes de continuar com a Movimentação solicite a ciência dos Custodiantes.'
                    }
                  </Typography>
                </div>
              </div>
            </>
          )}
          <Section title={'Detalhes da movimentação'} defaultGrid>
            <InstrumentoAutocomplete instrumentos={instrumentos} instrumento={instrumento} onChange={setInstrumento} />
            <Dropdown
              label="Tipo de Interoperabilidade"
              options={tipoInteroperabilidadeOptions}
              value={tipo}
              onChange={setTipo}
              required
            />
            <Dropdown
              label="Custodiante"
              options={custodianteOptions ?? []}
              value={custodianteId ?? ''}
              onChange={setCustodianteId}
              required
            />
            <Dropdown
              label="CPF/CNPJ Investidor"
              disabled={!instrumento}
              options={investidorOptions ?? []}
              value={investidorId ?? ''}
              onChange={setInvestidorId}
              required
            />
            <NumberField
              label="Quantidade"
              value={quantidade}
              onChange={(value) => setQuantidade(value ?? 0)}
              required
              disabled={!investidorId}
              numberType={getQuantityType(instrumento?.tipoInstrumentoFinanceiro)}
              helperText={
                !isNil(quantidadeMax) && tipo === TipoInteroperabilidade.Saida
                  ? `Quantidade disponível: ${formatNumber(quantidadeMax ?? 0, { minimumFractionDigits: 0, maximumFractionDigits: 8 })}`
                  : ''
              }
            />
            <NumberField
              label="PU"
              value={precoUnitario}
              onChange={(value) => setPrecoUnitario(value ?? 0)}
              required
              numberType="eightDecimals"
            />
          </Section>
          <Grid container justify="center">
            <Grid item>
              <Button variant="contained" color="primary" disabled={!formFulfilled} onClick={handleSubmit}>
                {'Comandar Transferência'}
              </Button>
              <LgSpace />
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
};

export default CadastrarOperacaoTransferencia;
